import { algoliasearch, SearchClient } from "algoliasearch";
import { getStatusChipStyle } from "@components/ui/dropdown/OrdreStatusDropdown";

const PROJECTS_INDEX = "projects";

let _client: SearchClient | null = null;

function getClient(): SearchClient {
  if (!_client) {
    const appId = process.env.REACT_APP_ALGOLIA_APP_ID || "";
    const searchKey = process.env.REACT_APP_ALGOLIA_SEARCH_KEY || "";
    if (!appId || !searchKey) {
      throw new Error("ALGOLIA_APP_ID and ALGOLIA_SEARCH_KEY must be set");
    }
    _client = algoliasearch(appId, searchKey);
  }
  return _client;
}

export async function searchProjects(
  query: string,
  orgId: string,
): Promise<string[]> {
  if (!query.trim()) return [];

  const result = await getClient().searchSingleIndex({
    indexName: PROJECTS_INDEX,
    searchParams: {
      query,
      filters: `org_id:${orgId}`,
      hitsPerPage: 100,
      attributesToRetrieve: ["objectID"],
    },
  });

  return result.hits.map((hit: any) => hit.objectID);
}

export interface ProjectHit {
  label: string;
  value: string;
  extra_label?: string;
  extra_label_2?: string;
  /** Coloured chip shown before extra_label (ordre status, …). */
  badge?: { label: string; bg: string; color: string };
  data?: Record<string, any>;
}

/** "dd/mm/yyyy" → epoch ms, 0 when missing/unparsable. */
function frDateToMs(value?: string): number {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(value || "");
  return m ? Date.UTC(Number(m[3]), Number(m[2]) - 1, Number(m[1])) : 0;
}

export async function searchPartnersFull(
  query: string,
  orgId: string,
): Promise<ProjectHit[]> {
  if (!query.trim()) return [];

  const result = await getClient().searchSingleIndex({
    indexName: "partners",
    searchParams: {
      query,
      filters: `org_id:${orgId}`,
      hitsPerPage: 10,
      attributesToRetrieve: ["objectID", "display_name", "company_name", "role", "company_types"],
    },
  });

  return result.hits.map((hit: any) => ({
    label: hit.company_name || hit.display_name || "",
    value: hit.objectID,
    extra_label: hit.role || "",
    extra_label_2: hit.display_name || "",
  }));
}

export async function searchProjectsFull(
  query: string,
  orgId: string,
): Promise<ProjectHit[]> {
  if (!query.trim()) return [];

  const result = await getClient().searchSingleIndex({
    indexName: PROJECTS_INDEX,
    searchParams: {
      query,
      filters: `org_id:${orgId}`,
      hitsPerPage: 10,
      attributesToRetrieve: ["objectID", "name", "num", "city", "address"],
    },
  });

  return result.hits.map((hit: any) => ({
    label: hit.name || "",
    value: hit.objectID,
    extra_label: [hit.num, hit.city].filter(Boolean).join(" · "),
    extra_label_2: hit.address || "",
  }));
}

/**
 * Make a plate-like query tokenize the same way stored plates do.
 * French plates are indexed as "AA-000-AA" → tokens [AA, 000, AA]. A user
 * typing a continuous "AA000AA" is a single token that prefix-matches none of
 * them, so the search only "worked" after deleting a char back to a boundary.
 * Re-inserting the AA-000-AA separators (only for plate-like input, i.e. mixed
 * letters+digits with no spaces) fixes progressive plate typing. Names/phones
 * are left untouched.
 */
export function normalizePlateQuery(query: string): string {
  const compact = query.replace(/[\s-]/g, "");
  const isPlateLike =
    /^[A-Za-z0-9]{2,9}$/.test(compact) && /[0-9]/.test(compact) && /[A-Za-z]/.test(compact);
  if (!isPlateLike) return query;
  const up = compact.toUpperCase();
  let out = "";
  for (let i = 0; i < up.length; i++) {
    if (i === 2 || i === 5) out += " ";
    out += up[i];
  }
  return out;
}

export async function searchDossiersFull(
  query: string,
  orgId: string,
): Promise<ProjectHit[]> {
  if (!query.trim()) return [];

  const result = await getClient().searchSingleIndex({
    indexName: "dossiers",
    searchParams: {
      query: normalizePlateQuery(query),
      filters: `org_id:${orgId}`,
      hitsPerPage: 10,
      attributesToRetrieve: ["objectID", "name", "type", "contact_names", "contact_phones", "vehicle_plates"],
    },
  });

  return result.hits.map((hit: any) => ({
    label: hit.name || "",
    value: hit.objectID,
    extra_label: [hit.type, ...(hit.contact_names || []).slice(0, 1), ...(hit.contact_phones || []).slice(0, 1)].filter(Boolean).join(" · "),
    extra_label_2: (hit.vehicle_plates || []).slice(0, 1).join(", ") || "",
  }));
}

export interface PlateHit {
  plate: string;
  marque?: string;
  modele?: string;
  dossier_id?: string;
  client_name?: string;
}

/**
 * Instant plate autocomplete straight from Algolia (dossiers index, plates are
 * a searchable attribute). Replaces the old backend /search-vehicles endpoint,
 * which scanned every client_vehicle + dossier in Firestore on each keystroke.
 * One dossier can hold several cars, so we expand each hit into its matching
 * plates and de-duplicate.
 */
export async function searchPlatesFull(
  query: string,
  orgId: string,
): Promise<PlateHit[]> {
  const norm = (p: string) => p.replace(/[^A-Za-z0-9]/g, "").toUpperCase();
  const q = norm(query);
  if (q.length < 2) return [];

  const result = await getClient().searchSingleIndex({
    indexName: "dossiers",
    searchParams: {
      query: normalizePlateQuery(query),
      filters: `org_id:${orgId}`,
      restrictSearchableAttributes: ["vehicle_plates"],
      hitsPerPage: 10,
      attributesToRetrieve: ["objectID", "name", "vehicle_plates", "vehicle_models"],
    },
  });

  const hits: PlateHit[] = [];
  const seen = new Set<string>();
  for (const hit of result.hits as any[]) {
    const plates: string[] = hit.vehicle_plates || [];
    const models: string[] = hit.vehicle_models || [];
    // vehicle_models is only index-aligned with plates when every vehicle has one.
    const aligned = plates.length === models.length;
    plates.forEach((plate, i) => {
      const np = norm(plate);
      if (!np.startsWith(q) || seen.has(np)) return;
      seen.add(np);
      const model = aligned ? (models[i] || "") : "";
      const [marque, ...rest] = model.split(" ");
      hits.push({
        plate,
        marque: marque || "",
        modele: rest.join(" "),
        dossier_id: hit.objectID,
        client_name: hit.name || "",
      });
    });
  }
  return hits.slice(0, 10);
}

export async function searchOrdresFull(
  query: string,
  orgId: string,
): Promise<ProjectHit[]> {
  if (!query.trim()) return [];

  // Numéro d'OR tapé (« 1187176 », « OR-1187176 », « or1187176 ») : on cherche les
  // chiffres seuls, sans tolérance aux fautes — sinon 1185176 / OR-1187276 sortent
  // aussi — et l'ordre dont le numéro correspond passe devant le tri par date.
  const orDigits = query.trim().match(/^(?:OR[\s-]*)?(\d{4,})$/i)?.[1];

  const result = await getClient().searchSingleIndex({
    indexName: "ordres",
    searchParams: {
      query: orDigits ?? query,
      ...(orDigits ? { typoTolerance: false } : {}),
      filters: `org_id:${orgId}`,
      hitsPerPage: 20,
      attributesToRetrieve: ["objectID", "num", "or_number", "alpha_or_number", "name", "status", "immatriculation", "reception_date_str", "restitution_str", "created_at_ts"],
    },
  });

  // Most recent first. Algolia ranks by relevance, which mixed a 2024 ordre in
  // between this week's ones; the list shows the réception date, so that is what
  // it sorts on (falling back to the creation date when the RDV has none).
  return result.hits
    .map((hit: any) => {
      const dates = [
        hit.reception_date_str ? `Réc. ${hit.reception_date_str}` : "",
        hit.restitution_str ? `Rest. ${hit.restitution_str}` : "",
      ].filter(Boolean).join(" · ");
      const chip = hit.status ? getStatusChipStyle(hit.status) : null;
      return {
        label: [hit.num, hit.name].filter(Boolean).join(" — "),
        value: hit.objectID,
        badge: chip || undefined,
        extra_label: hit.immatriculation || "",
        extra_label_2: dates,
        _recency: frDateToMs(hit.reception_date_str)
          || (typeof hit.created_at_ts === "number" ? hit.created_at_ts * 1000 : 0),
        // L'OR créé par le transfert Alpha (alpha_or_number) compte aussi : `num` peut
        // encore porter le provisoire TEMP-… tant que ServiceBox ne l'a pas repris.
        _exact: !!orDigits && [hit.num, hit.or_number, hit.alpha_or_number]
          .some((n) => String(n || "").replace(/\D/g, "") === orDigits),
      };
    })
    .sort((a, b) => Number(b._exact) - Number(a._exact) || b._recency - a._recency)
    .map(({ _recency, _exact, ...hit }) => hit);
}

/** `hitsPerPage` : 10 suffit pour une liste de suggestions, mais l'écran Véhicules
 *  FILTRE son parc sur ces résultats — il lui faut tous les véhicules qui matchent,
 *  pas les dix premiers. */
export async function searchVehiclesFull(
  query: string,
  orgId: string,
  hitsPerPage = 10,
): Promise<ProjectHit[]> {
  if (!query.trim()) return [];

  const result = await getClient().searchSingleIndex({
    indexName: "vehicles",
    searchParams: {
      query,
      filters: `org_id:${orgId}`,
      hitsPerPage,
      attributesToRetrieve: ["objectID", "plate", "name", "marque", "modele", "status"],
    },
  });

  return result.hits.map((hit: any) => ({
    label: [hit.plate, hit.name].filter(Boolean).join(" — "),
    value: hit.objectID,
    extra_label: [hit.marque, hit.modele].filter(Boolean).join(" "),
    extra_label_2: hit.status || "",
  }));
}

export async function searchJobsFull(
  query: string,
  orgId: string,
): Promise<ProjectHit[]> {
  if (!query.trim()) return [];

  const result = await getClient().searchSingleIndex({
    indexName: "workshop_jobs",
    searchParams: {
      query,
      filters: `org_id:${orgId}`,
      hitsPerPage: 10,
      attributesToRetrieve: [
        "objectID", "client_name", "plate", "vehicle_name",
        "worker_name", "worker_id", "date", "start_hour", "specialty",
        "status", "interventions", "ordre_id", "ordre_num",
      ],
    },
  });

  return result.hits.map((hit: any) => {
    const dateFormatted = hit.date
      ? hit.date.split("-").reverse().join("/")
      : "";
    const interventionLabel = (hit.interventions || []).slice(0, 2).join(", ");
    return {
      label: [hit.plate, hit.client_name].filter(Boolean).join(" — "),
      value: hit.objectID,
      extra_label: [hit.ordre_num, interventionLabel || hit.specialty, hit.worker_name, dateFormatted].filter(Boolean).join(" · "),
      extra_label_2: hit.vehicle_name || "",
      data: { ordre_id: hit.ordre_id, date: hit.date, worker_id: hit.worker_id },
    };
  });
}
